/// <reference types="@angular/localize" />

import { inject, LOCALE_ID, Service } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, NEVER, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { authenticationControllerLogoutV1, authenticationControllerRefreshV1 } from '@lexplus-authentication-api';
import { ImpersonationState, notify, ProfileState, redactSecrets } from '@lexplus-shared-lib';
import { API_CONFIGURATION } from '@lexplus-environments';
import { SESSION_EXPIRED_TOAST_ID } from '../auth-notifications.const';

@Service({ autoProvided: false })
export class ApiTokenInterceptor implements HttpInterceptor {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly localeSprache = inject(LOCALE_ID);

  /** Header a Neosys admin uses to pick the Kunde to impersonate. */
  private readonly impersonateKundeHeader = 'x-impersonate-kunde';

  /**
   * Intercepts every HTTP request. If an access token exists, it will be attached to the request as Authorization Header
   * in the format of "Bearer ${Token}". If a http 401 exception occurs, it will attempt to get new tokens via refresh token
   * mechanism. If this fails, the user will be automatically logged out, because the refresh token is invalid or expired.
   *
   * @param request
   * @param next
   */
  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // add language too
    let headers = request.headers.set('Accept-Language', this.localeSprache);
    const impersonatedKundeId = this.store.selectSnapshot(ImpersonationState.kundeID);
    if (impersonatedKundeId != null) {
      headers = headers.set(this.impersonateKundeHeader, impersonatedKundeId.toString());
    }

    request = request.clone({
      withCredentials: true,
      headers,
    });

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && this.store.selectSnapshot(ProfileState.isLoggedIn)) {
          /**
           * Logout the user if the 401 error stems from refresh token endpoints (=> refresh token has expired)
           */
          if (request.url.indexOf(authenticationControllerRefreshV1.PATH) > -1) {
            // The refresh itself returned 401 → the refresh token has expired: log out
            // and rethrow, do NOT refresh again (that would loop forever).
            void this.router.navigateByUrl('/auth/logout');
            notify.warning($localize`:@@auth.inactivity.loggedOut:Abgemeldet`, {
              id: SESSION_EXPIRED_TOAST_ID,
              description: $localize`:@@auth.inactivity.loggedOutDescription:Aufgrund zu langer Inaktivität erfolgte eine automatische Abmeldung.`,
            });
            return throwError(() => error);
          }
          // A 401 on the logout itself means the session is already over. Refreshing would end
          // in `NEVER` once the refresh fails, and the logout page would wait on it forever.
          if (request.url.indexOf(authenticationControllerLogoutV1.PATH) > -1) {
            return throwError(() => error);
          }
          return this.handle401Error(request, next);
        } else if (error instanceof HttpErrorResponse && (error.status === 502 || error.status === 0)) {
          notify.error($localize`:@@auth.api.restarting:API wird neu gestartet`, {
            description: $localize`:@@auth.api.restartingDescription:API wird neu gestartet. Bitte in 30 Sekunden die Seite neu laden.`,
          });
          throw error;
        } else if (error instanceof HttpErrorResponse && error.status !== 401) {
          const isClientError = error.status >= 400 && error.status < 500;

          Sentry.captureException(error, {
            tags: {
              url: request.url,
              method: request.method,
              status: error.status.toString(),
            },
            extra: {
              requestBody: redactSecrets(request.body),
              response: redactSecrets(error.error),
            },
            level: isClientError ? 'warning' : 'error',
          });

          // A client error belongs to the caller, which surfaces it next to the form the
          // user is still working in. Only an unexpected server failure replaces the whole page.
          if (!isClientError) {
            void this.router.navigate(['/error'], { queryParams: { errorNumber: error.status } });
          }

          throw error;
        } else {
          throw error;
        }
      }),
    );
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(false);
      /**
       * Get new access_token and refresh_token based on the current refresh_token.
       */
      return authenticationControllerRefreshV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {
        body: {
          appDeviceId: this.store.selectSnapshot(ProfileState.appDeviceId),
        },
      }).pipe(
        switchMap(() => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(true);
          return next.handle(request);
        }),
        catchError(() => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(false);
          // The refresh failed, so the session is over. The branch in `intercept` has
          // already navigated to the logout route and told the user why, so the original
          // request's failure is collateral: rethrowing it made whichever page was loading
          // raise a second toast carrying the API's own «Refresh token is missing.». The
          // queued requests in the `else` below already stall for the same reason — the
          // retry gate never opens once the refresh fails.
          return NEVER;
        }),
      );
    } else {
      // Wait until the token refresh has finished, then retry the request.
      return this.refreshTokenSubject.pipe(
        filter((tokenRefreshed) => tokenRefreshed),
        take(1),
        switchMap(() => {
          return next.handle(request);
        }),
      );
    }
  }
}
